/**
 * SupportCaseCreateForm
 * ---------------------------------------------------------------------------
 * Logging a NEW support case, or a platform incident, from the queue. The queue
 * could already triage, assign and resolve; this is the missing create path.
 *
 * Rendered as a react-bootstrap Modal over SupportCaseQueue, the same pattern
 * SupportCaseDetailPanel already uses (Modal with show / onHide, an Alert in the
 * body for the server's refusal, a Spinner on the submit button), so there is
 * one modal implementation in this feature and not two.
 *
 * WHO SEES WHAT. This component does not decide. `isStaff` is passed in by
 * SupportCaseQueue, which computes it exactly once with its own isPlatformAdmin
 * helper, and the same flag is handed to buildCreateCasePayload so a field that
 * is hidden is also never sent:
 *
 *   staff           subject, description, category, priority, order id,
 *                   vendor id, platform incident, evidence links
 *   vendor manager  subject, description, category, order id, evidence links
 *
 * A vendor manager gets no priority select because the server derives a priority
 * when none is sent, no incident checkbox because an incident is a platform-wide
 * call, and no vendor id field because the server scopes them to their own
 * vendor regardless of what a client asks for.
 *
 * The SERVER is authoritative. The validation here matches the model's limits
 * (subject 140, description 4000) as a courtesy so a typo does not cost a round
 * trip, and every real refusal is the server's: `{ message, code }` is rendered
 * at the top of the form and, when the message names one, next to the field it
 * is about.
 *
 * "case" and "incident", never "ticket": MessagingTicket and event box-office
 * ticketing are both something else in this repo.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { apiCreateSupportCase, supportCaseErrorMessage } from "../api/supportCases";
import {
  EMPTY_CASE_DRAFT,
  SHIPPED_CASE_VOCABULARY,
  SUPPORT_CASE_DESCRIPTION_MAX,
  SUPPORT_CASE_SUBJECT_MAX,
  buildCreateCasePayload,
  caseCategoryLabel,
  caseCreateFieldFromMessage,
  casePriorityLabel,
  normalizeCasePayload,
  parseEvidenceUrls,
  toCaseOptions,
  validateCaseDraft,
} from "../utilities/supportCaseVocabulary";
import AppSelect from "../components/ui/AppSelect";

/** Under-field message, for a local check or for the server's own words. */
const FieldError = ({ children }) =>
  children ? <div className="support-case-field-error">{children}</div> : null;

/** "12 / 140", turning red once the limit the model enforces is passed. */
const Counter = ({ value, max }) => {
  const length = String(value == null ? "" : value).length;
  return (
    <div className={`support-case-counter${length > max ? " is-over" : ""}`}>
      {length} / {max}
    </div>
  );
};

const SupportCaseCreateForm = ({
  show,
  onHide,
  onCreated,
  isStaff = false,
  vocabulary = SHIPPED_CASE_VOCABULARY,
  defaultOrderId = "",
  defaultVendorId = "",
}) => {
  // State first, then every memo that reads it: a memo declared above its state
  // is the no-use-before-define this repo's lint gate fails on.
  const [draft, setDraft] = useState(EMPTY_CASE_DRAFT);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [errorCode, setErrorCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const categoryOptions = useMemo(
    () => toCaseOptions(vocabulary.categories, caseCategoryLabel),
    [vocabulary.categories]
  );
  const priorityOptions = useMemo(
    () => toCaseOptions(vocabulary.priorities, casePriorityLabel),
    [vocabulary.priorities]
  );

  const evidence = useMemo(() => parseEvidenceUrls(draft.evidenceUrls), [draft.evidenceUrls]);

  // A fresh open starts from a clean draft rather than the last attempt, and
  // picks up whatever order or vendor the caller opened the form against.
  useEffect(() => {
    if (!show) return;
    setDraft({
      ...EMPTY_CASE_DRAFT,
      orderId: defaultOrderId || "",
      vendorId: isStaff ? defaultVendorId || "" : "",
    });
    setFieldErrors({});
    setFormError("");
    setErrorCode("");
    setSubmitting(false);
  }, [show, defaultOrderId, defaultVendorId, isStaff]);

  const setField = useCallback((key, value) => {
    setDraft((previous) => ({ ...previous, [key]: value }));
    // Clearing the error on edit keeps a stale server complaint from sitting
    // under a field the person has already fixed.
    setFieldErrors((previous) => {
      if (!previous[key]) return previous;
      const next = { ...previous };
      delete next[key];
      return next;
    });
  }, []);

  const submit = useCallback(async () => {
    const options = {
      staff: isStaff,
      categories: vocabulary.categories,
      priorities: vocabulary.priorities,
    };
    const check = validateCaseDraft(draft, options);
    if (!check.valid) {
      setFieldErrors(check.errors);
      setFormError("Some details still need fixing.");
      setErrorCode("");
      return;
    }

    setSubmitting(true);
    setFieldErrors({});
    setFormError("");
    setErrorCode("");
    try {
      const response = await apiCreateSupportCase(buildCreateCasePayload(draft, options));
      const created = normalizeCasePayload(response?.data);
      // The queue refreshes and opens the new case. It is told even when the
      // create response arrived in an envelope this client did not expect, so a
      // logged case never goes unseen: a null just means refetch and do not open.
      if (onCreated) onCreated(created);
      if (onHide) onHide();
    } catch (createError) {
      const message = supportCaseErrorMessage(createError, "The case was not logged.");
      const code = createError?.response?.data?.code || "";
      setFormError(message);
      setErrorCode(code);
      const field = caseCreateFieldFromMessage(message);
      if (field) setFieldErrors({ [field]: message });
    } finally {
      setSubmitting(false);
    }
  }, [draft, isStaff, vocabulary.categories, vocabulary.priorities, onCreated, onHide]);

  return (
    <Modal
      show={show}
      onHide={submitting ? () => {} : onHide}
      size="lg"
      className="support-case-create"
      scrollable
    >
      <Modal.Header closeButton>
        <Modal.Title as="div" style={{ width: "100%" }}>
          <div className="support-case-eyebrow">Support</div>
          <div style={{ fontSize: "1.05rem", fontWeight: 700 }}>
            {draft.isIncident ? "Log a platform incident" : "Log a case"}
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {formError ? (
          <Alert variant="danger" onClose={() => setFormError("")} dismissible>
            {formError}
            {errorCode ? <div className="support-case-meta">{errorCode}</div> : null}
          </Alert>
        ) : null}

        <div className="support-case-section">
          <div className="support-case-section-title">What happened</div>

          <Form.Label>Subject</Form.Label>
          <Form.Control
            size="sm"
            placeholder="One line a handler can triage from"
            value={draft.subject}
            maxLength={SUPPORT_CASE_SUBJECT_MAX}
            isInvalid={Boolean(fieldErrors.subject)}
            onChange={(event) => setField("subject", event.target.value)}
            disabled={submitting}
          />
          <Counter value={draft.subject} max={SUPPORT_CASE_SUBJECT_MAX} />
          <FieldError>{fieldErrors.subject}</FieldError>

          <Form.Label style={{ marginTop: 8 }}>Description</Form.Label>
          <Form.Control
            as="textarea"
            rows={6}
            size="sm"
            placeholder="What went wrong, when, and what has been tried so far"
            value={draft.description}
            maxLength={SUPPORT_CASE_DESCRIPTION_MAX}
            isInvalid={Boolean(fieldErrors.description)}
            onChange={(event) => setField("description", event.target.value)}
            disabled={submitting}
          />
          <Counter value={draft.description} max={SUPPORT_CASE_DESCRIPTION_MAX} />
          <FieldError>{fieldErrors.description}</FieldError>
        </div>

        <div className="support-case-section">
          <div className="support-case-section-title">How to handle it</div>
          <Row className="g-2">
            <Col md={isStaff ? 6 : 12} sm={12}>
              <Form.Label>Category</Form.Label>
              <AppSelect block
                size="sm"
                value={draft.category}
                isInvalid={Boolean(fieldErrors.category)}
                onChange={(event) => setField("category", event.target.value)}
                disabled={submitting}
              >
                <option value="">Choose a category</option>
                {categoryOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </AppSelect>
              <FieldError>{fieldErrors.category}</FieldError>
            </Col>

            {/* Staff only. Left blank, the server derives the priority from the
                category, the order total and whether this is an incident. */}
            {isStaff ? (
              <Col md={6} sm={12}>
                <Form.Label>Priority</Form.Label>
                <AppSelect block
                  size="sm"
                  value={draft.priority}
                  isInvalid={Boolean(fieldErrors.priority)}
                  onChange={(event) => setField("priority", event.target.value)}
                  disabled={submitting}
                >
                  <option value="">Let the server decide</option>
                  {priorityOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </AppSelect>
                <FieldError>{fieldErrors.priority}</FieldError>
              </Col>
            ) : null}
          </Row>

          {isStaff ? (
            <div style={{ marginTop: 10 }}>
              <Form.Check
                type="checkbox"
                id="support-case-create-incident"
                label="This is a platform incident"
                checked={draft.isIncident === true}
                onChange={(event) => setField("isIncident", event.target.checked)}
                disabled={submitting}
              />
              <div className="support-case-field-hint">
                An incident is platform wide rather than one customer's case, and the server
                treats it as urgent.
              </div>
              <FieldError>{fieldErrors.isIncident}</FieldError>
            </div>
          ) : null}
        </div>

        <div className="support-case-section">
          <div className="support-case-section-title">What it is about</div>
          <Row className="g-2">
            <Col md={6} sm={12}>
              <Form.Label>Order id</Form.Label>
              <Form.Control
                size="sm"
                placeholder="Optional"
                value={draft.orderId}
                isInvalid={Boolean(fieldErrors.orderId)}
                onChange={(event) => setField("orderId", event.target.value)}
                disabled={submitting}
              />
              <FieldError>{fieldErrors.orderId}</FieldError>
            </Col>
            {/* Staff only. A vendor manager is scoped to their own vendor by the
                server, so there is nothing for them to choose here. */}
            {isStaff ? (
              <Col md={6} sm={12}>
                <Form.Label>Vendor id</Form.Label>
                <Form.Control
                  size="sm"
                  placeholder="Optional"
                  value={draft.vendorId}
                  isInvalid={Boolean(fieldErrors.vendorId)}
                  onChange={(event) => setField("vendorId", event.target.value)}
                  disabled={submitting}
                />
                <FieldError>{fieldErrors.vendorId}</FieldError>
              </Col>
            ) : null}
          </Row>
          <div className="support-case-field-hint">
            A case can hang off an order, off a vendor, off both, or off neither.
          </div>
        </div>

        <div className="support-case-section">
          <div className="support-case-section-title">Evidence</div>
          <Form.Label>Links</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            size="sm"
            placeholder="One web address per line. Screenshots, a receipt, a delivery photo."
            value={draft.evidenceUrls}
            isInvalid={Boolean(fieldErrors.evidenceUrls)}
            onChange={(event) => setField("evidenceUrls", event.target.value)}
            disabled={submitting}
          />
          <FieldError>{fieldErrors.evidenceUrls}</FieldError>
          {evidence.evidence.length ? (
            <ul className="support-case-evidence-preview">
              {evidence.evidence.map((item) => (
                <li key={item.url}>
                  <span className="support-case-meta">{item.type}</span> {item.url}
                </li>
              ))}
            </ul>
          ) : (
            <div className="support-case-field-hint">Optional.</div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={submit} disabled={submitting}>
          {submitting ? (
            <Spinner animation="border" size="sm" />
          ) : draft.isIncident ? (
            "Log incident"
          ) : (
            "Log case"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SupportCaseCreateForm;
